import { motion } from "framer-motion";
import { Check } from "lucide-react";

export const EasySyncMockup = () => {
  return (
    <div className="w-28 h-28 md:w-36 md:h-36 rounded-2xl bg-gradient-to-br from-blue-600 via-blue-500 to-cyan-400 p-3 md:p-4 flex flex-col justify-between shadow-xl overflow-hidden relative">
      {/* Ambient glow */}
      <div className="absolute inset-0 bg-gradient-to-t from-white/10 to-transparent" />
      <div className="absolute -top-8 -right-8 w-24 h-24 bg-white/10 rounded-full blur-2xl" />
      
      {/* Source icons row */}
      <div className="flex justify-center gap-2 relative z-10">
        {/* Gmail icon */}
        <motion.div
          initial={{ scale: 0, y: -10 }}
          whileInView={{ scale: 1, y: 0 }}
          transition={{ delay: 0.1, type: "spring", stiffness: 300 }}
          className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-white/95 backdrop-blur shadow-lg flex items-center justify-center"
        >
          <svg className="w-4 h-4 md:w-5 md:h-5" viewBox="0 0 24 24">
            <path fill="#EA4335" d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
          </svg>
        </motion.div>
        
        {/* Dropbox icon */}
        <motion.div
          initial={{ scale: 0, y: -10 }}
          whileInView={{ scale: 1, y: 0 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 300 }}
          className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-white/95 backdrop-blur shadow-lg flex items-center justify-center"
        >
          <svg className="w-4 h-4 md:w-5 md:h-5" viewBox="0 0 24 24">
            <path fill="#0061FF" d="M12 6.134L6.069 9.797 12 13.459l5.931-3.662L12 6.134zM6.069 14.134L12 17.797l5.931-3.663L12 10.472l-5.931 3.662zM0 9.797l6.069 3.663L12 9.797 6.069 6.134 0 9.797zm12 0l6.069 3.663L24 9.797l-6.069-3.663L12 9.797z"/>
          </svg>
        </motion.div>
      </div>

      {/* Animated documents flowing down */}
      <div className="relative flex-1 flex items-center justify-center my-1">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            initial={{ y: -15, opacity: 0, scale: 0.8 }}
            whileInView={{ y: 12, opacity: 1, scale: 1 }}
            transition={{
              delay: 0.4 + i * 0.12,
              duration: 0.5,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="absolute"
            style={{ left: `calc(50% + ${(i - 1) * 14}px - 10px)` }}
          >
            <div 
              className="w-5 h-6 md:w-6 md:h-7 bg-white rounded-md shadow-lg relative overflow-hidden"
              style={{ 
                transform: `rotate(${(i - 1) * 3}deg)`,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}
            >
              {/* Document content */}
              <div className="p-1 space-y-0.5">
                <div className="w-3 h-0.5 bg-blue-300 rounded-full" />
                <div className="w-2 h-0.5 bg-blue-200 rounded-full" />
                <div className="w-2.5 h-0.5 bg-blue-200 rounded-full" />
              </div>
              {/* PDF badge for middle doc */}
              {i === 1 && (
                <div className="absolute bottom-0.5 right-0.5 text-[5px] font-bold text-red-500 bg-red-50 px-0.5 rounded">
                  PDF
                </div>
              )}
            </div>
          </motion.div>
        ))}
        
        {/* Flow arrows */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="absolute top-0 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 3, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            className="text-white/40 text-xs"
          >
            ↓
          </motion.div>
        </motion.div>
      </div>

      {/* Progress bar with glassmorphism */}
      <div className="relative z-10">
        <div className="h-2 bg-white/20 backdrop-blur-sm rounded-full overflow-hidden border border-white/10">
          <motion.div
            initial={{ width: 0 }}
            whileInView={{ width: "100%" }}
            transition={{ delay: 0.7, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="h-full bg-gradient-to-r from-white/90 to-white rounded-full relative"
          >
            {/* Shimmer effect */}
            <motion.div
              animate={{ x: [-20, 80] }}
              transition={{ delay: 1.5, duration: 0.6, ease: "easeOut" }}
              className="absolute inset-0 w-8 bg-gradient-to-r from-transparent via-white to-transparent opacity-50"
            />
          </motion.div>
        </div>
        
        {/* Success indicator */}
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          transition={{ delay: 1.5, type: "spring", stiffness: 400 }}
          className="absolute -right-1 -top-1 w-4 h-4 bg-emerald-400 rounded-full flex items-center justify-center shadow-lg"
        >
          <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
        </motion.div>
      </div>
    </div>
  );
};
